/**
 * An invitations list (`GET …/invitations?status=`, not paged) with a status filter (Pending by
 * default) and, for a row the signed-in member may act on, Resend and Revoke (confirmed: a revoked
 * invitation cannot be restored). Each action refetches the list. Shared by Team (colleague
 * invitations) and People (tenant invitations).
 */
import { useCallback, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { INVITATION_STATUSES, type Invitation } from '@/api/saasAuthReseller';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ApiErrorState } from '@/components/ApiErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/useToast';
import { enumParam, useUrlState } from '@/components/useUrlState';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useLocale } from '@/i18n/useLocale';
import { useMemberError, useMemberErrorMessage, type MemberError } from './memberErrors';
import { useMenuFocusReturn } from './useMenuFocusReturn';
import { StatusChip } from '@/components/status/StatusChip';
import { dayOf, invitationStatusLabel, roleLabel } from './memberLabels';
import { useRemote } from '@/components/useRemote';
import { FIELD_WIDTH } from '@/theme';

const ALL = 'ALL';
const DEFAULT_STATUS = 'PENDING';

/** The status filter's URL param (TMM-B6); the tab that holds the table clears it on a switch. */
export const INVITATIONS_PARAM = 'invitations';

const SCHEMA = {
  [INVITATIONS_PARAM]: enumParam([ALL, ...INVITATION_STATUSES], DEFAULT_STATUS),
};

export interface InvitationsTableProps {
  list: (status: string | undefined) => Promise<Invitation[]>;
  resend: (id: number) => Promise<void>;
  revoke: (id: number) => Promise<void>;
  /** Whether Resend / Revoke show on this row (a write role, a pending invitation, …). */
  canAct: (invitation: Invitation) => boolean;
  /** Opens Invite. Omitted for a read-only role, which then sees no action. */
  onInvite?: () => void;
}

export function InvitationsTable({ list, resend, revoke, canAct, onInvite }: InvitationsTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const showToast = useToast();
  const errorMessage = useMemberErrorMessage();
  const memberError = useMemberError();
  const url = useUrlState(SCHEMA);
  const status = url.values[INVITATIONS_PARAM];
  const headingId = useId();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<MemberError | null>(null);
  const [revoking, setRevoking] = useState<Invitation | null>(null);
  // A revoked invitation's row no longer offers Revoke, so focus returns to the heading (DLG-004).
  const heading = useRef<HTMLHeadingElement>(null);
  const focusReturn = useMenuFocusReturn(heading);
  const load = useCallback(() => list(status === ALL ? undefined : status), [list, status]);
  const { status: loadStatus, data, error, reload } = useRemote(load);
  const rows = data ?? [];

  async function act(id: number, run: (id: number) => Promise<void>, doneKey: string) {
    setBusyId(id);
    setActionError(null);
    try {
      await run(id);
      showToast(t(doneKey));
      reload();
    } catch (err) {
      setActionError(memberError(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Box>
      <Typography
        variant="h2"
        component="h2"
        id={headingId}
        tabIndex={-1}
        ref={heading}
        sx={visuallyHidden}
      >
        {t('members.invitations.heading')}
      </Typography>
      <TextField
        select
        size="small"
        label={t('members.invitations.filter')}
        value={status}
        onChange={(event) =>
          url.set({ [INVITATIONS_PARAM]: event.target.value as (typeof status) })
        }
        sx={{ mb: 2, minWidth: FIELD_WIDTH.sm }}
      >
        <MenuItem value={ALL}>{t('members.invitations.all')}</MenuItem>
        {INVITATION_STATUSES.map((value) => (
          <MenuItem key={value} value={value}>
            {invitationStatusLabel(value, t)}
          </MenuItem>
        ))}
      </TextField>
      <ActionErrorAlert error={actionError} sx={{ mb: 2 }} />
      {loadStatus === 'error' ? <ApiErrorState error={error} onRetry={reload} /> : null}
      {loadStatus === 'loading' && data === null ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={160} />
        </Box>
      ) : null}
      {/* Pending (the default) and All show "none yet"; another status hides rows that exist. */}
      {loadStatus !== 'error' && data !== null && rows.length === 0 ? (
        status === ALL || status === DEFAULT_STATUS ? (
          <EmptyState
            kind="none"
            title={
              status === ALL
                ? t('members.invitations.none')
                : t('members.invitations.nonePending')
            }
            action={
              onInvite ? (
                <Button variant="contained" onClick={onInvite}>
                  {t('members.invitations.invite')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <EmptyState
            kind="noMatch"
            title={t('members.invitations.noMatch')}
            onClear={() => url.set({ [INVITATIONS_PARAM]: DEFAULT_STATUS })}
          />
        )
      ) : null}
      {loadStatus !== 'error' && rows.length > 0 ? (
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-labelledby={headingId}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('members.col.email')}</TableCell>
                  <TableCell>{t('members.col.role')}</TableCell>
                  <TableCell>{t('members.col.status')}</TableCell>
                  <TableCell>{t('members.col.sent')}</TableCell>
                  <TableCell>{t('members.col.expires')}</TableCell>
                  <TableCell align="right">{t('members.col.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell component="th" scope="row" sx={{ overflowWrap: 'anywhere' }}>
                      {invitation.email}
                    </TableCell>
                    <TableCell>{roleLabel(invitation.role, t)}</TableCell>
                    <TableCell>
                      <StatusChip domain="invitation" status={invitation.status} />
                    </TableCell>
                    <TableCell>{dayOf(invitation.createdAt, locale, t)}</TableCell>
                    <TableCell>{dayOf(invitation.expiresAt, locale, t)}</TableCell>
                    <TableCell align="right">
                      {canAct(invitation) ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            disabled={busyId !== null}
                            aria-label={t('members.invitations.resendFor', {
                              email: invitation.email,
                            })}
                            onClick={() => void act(invitation.id, resend, 'members.toast.resent')}
                          >
                            {t('members.invitations.resend')}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={busyId !== null}
                            aria-label={t('members.invitations.revokeFor', {
                              email: invitation.email,
                            })}
                            onClick={(event) => {
                              focusReturn.remember(event.currentTarget);
                              setRevoking(invitation);
                            }}
                          >
                            {t('members.invitations.revoke')}
                          </Button>
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      ) : null}
      <ConfirmDialog
        open={revoking !== null}
        title={t('members.invitations.revokeTitle', { email: revoking?.email ?? '' })}
        consequence={t('members.invitations.revokeBody')}
        confirmLabel={t('members.invitations.revoke')}
        destructive
        onConfirm={async () => {
          if (!revoking) return;
          await revoke(revoking.id);
          showToast(t('members.toast.revoked'));
          reload();
          focusReturn.rowGone();
        }}
        onCancel={() => {
          setRevoking(null);
          focusReturn.dialogClosed();
        }}
        errorMessage={errorMessage}
      />
    </Box>
  );
}
